import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import {
    getImportedAccountMappingResolution,
    ImportedAccountMappingResolution,
} from "@common/helpers/importedAccountMappingResolution";
import { Button } from "@design-system/Button/Button";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { AccountMappingStatus } from "../../AccountMappingStatus";
import { CoaMappingRow } from "../coaMappingRowTypes";
import { useCoaMappingCellData } from "./useCoaMappingCellData";

export const CoaMappingStatusCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, CoaMappingRow, unknown>) => {
        const { account, kickAccount, options, callbacks } =
            useCoaMappingCellData({ row: row.original, table });
        if (!account) {
            return <ViewTableCell />;
        }
        const resolution = getImportedAccountMappingResolution({ account });
        const isUnmapped =
            resolution === ImportedAccountMappingResolution.UNMAPPED &&
            !account.accountConnectionTaskId;
        if (isUnmapped && options?.isCreateAccountAvailable) {
            return (
                <ViewTableCell captureClicks>
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                            callbacks?.onOpenCreateAccountModal({
                                importedAccountId: account.id,
                            })
                        }
                    >
                        Create account
                    </Button>
                </ViewTableCell>
            );
        }
        return (
            <ViewTableCell>
                <AccountMappingStatus
                    row={{
                        kickAccountId: kickAccount?.id ?? null,
                        createNewAccount:
                            resolution ===
                            ImportedAccountMappingResolution.NEW_ACCOUNT,
                        accountConnectionTaskId:
                            account.accountConnectionTaskId,
                        financialType:
                            resolution ===
                            ImportedAccountMappingResolution.UNMAPPED
                                ? null
                                : account.financialType,
                        detectedInstitutionName:
                            account.detectedInstitutionName,
                    }}
                />
            </ViewTableCell>
        );
    },
);

CoaMappingStatusCell.displayName = "CoaMappingStatusCell";
